import { AppMessage } from '@/common/components';
import { Form } from 'antd';
import { FC, memo, useCallback, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router';


import { AppRoutes, FormFieldsErrors } from '@/common/interfaces';
import { FallbackedLayout } from '@/common/layouts';
import { extractFormErrors, messageOnError } from '@/common/utils';
import { PageLayoutsFormFields, PageLayoutsFormInputs, PageLayoutsQueryKeys, useEditLayoutsPageMutation, useGetByIdLayoutsPageQuery } from '@/modules/Pages';

import { PageLayoutsFormView } from './PageLayoutsFormView';
import { pageLayoutsFormValidationSchema } from './validation';

interface Props {
  siteId: string;
  layoutId: string;
}

export const PageLayoutsEditFormContainer: FC<Props> = memo(({ layoutId, siteId }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const appMessageApi = AppMessage.useControls();
  const [form] = Form.useForm<PageLayoutsFormFields>();
  const [formErrors, setFormErrors] = useState<FormFieldsErrors<typeof PageLayoutsFormInputs>>(null);

  const { data: layoutData, isLoading: isLayoutLoading, error: layoutError, isError: isLayoutError } = useGetByIdLayoutsPageQuery(layoutId, siteId)();

  const { mutate: editLayoutMutate, isLoading: isEditLayoutLoading } = useEditLayoutsPageMutation(layoutId, siteId)({
    onSuccess: async () => {
      appMessageApi.success('Макет успішно оновлено!');

      await queryClient.refetchQueries([PageLayoutsQueryKeys.GetAll, siteId], { exact: true });

      navigate(AppRoutes.PageLayoutsById.replace(':siteId', siteId).replace(':layoutId', layoutId));
    },
    onError: (err) => {
      setFormErrors(extractFormErrors<typeof PageLayoutsFormInputs>(err));
      messageOnError(err, appMessageApi);
    }
  });

  const initialValues: PageLayoutsFormFields = {
    name: layoutData?.name,
    content: layoutData?.content
  };

  const handleFinish = useCallback((values: PageLayoutsFormFields): void => {
    setFormErrors(null);

    editLayoutMutate(values);
  }, []);

  const handleBack = useCallback((): void => {
    navigate(AppRoutes.PageLayoutsById.replace(':siteId', siteId).replace(':layoutId', layoutId));
  }, [siteId, layoutId]);

  const handleFinishFailed = useCallback((): void => {
    setFormErrors(null);
  }, []);

  return (
    <FallbackedLayout
      isLoading={ isLayoutLoading }
      isError={ isLayoutError }
      error={ layoutError }
    >
      {
        () => (
          <PageLayoutsFormView
            form={ form }
            initialValues={ initialValues }
            formErrors={ formErrors }
            formName="page-layouts-edit-form"
            formType="edit"
            handleBack={ handleBack }
            handleFinish={ handleFinish }
            handleFinishFailed={ handleFinishFailed }
            isFormLoading={ isEditLayoutLoading }
            validationSchema={ pageLayoutsFormValidationSchema }
          />
        )
      }
    </FallbackedLayout>
  );
});
